import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProviderSidebar from "../../provider/components/ProviderSidebar";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from "@/lib/notifications";

function typeIcon(type: string): string {
  if (type === "booking") return "ri-calendar-check-line";
  if (type === "review") return "ri-star-line";
  if (type === "document") return "ri-file-shield-line";
  return "ri-notification-3-line";
}

function relativeTime(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("provider.notif.justNow");
  if (mins < 60) return t("provider.notif.minsAgo", { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("provider.notif.hoursAgo", { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("provider.notif.daysAgo", { n: days });
  return d.toLocaleDateString("es-MX");
}

export default function ProveedorNotificacionesPage() {
  const { t } = useTranslation("provider");
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const load = () => {
    setLoading(true);
    listNotifications()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onOpen = async (n: AppNotification) => {
    if (!n.isRead) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      try { await markNotificationRead(n.id); } catch { /* optimistic */ }
    }
    if (n.linkUrl) navigate(n.linkUrl);
  };

  const onReadAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    try { await markAllNotificationsRead(); } catch { load(); }
  };

  const filtered = filter === "all" ? items : items.filter((n) => !n.isRead);
  const hasUnread = items.some((n) => !n.isRead);

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="w-full">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ProviderSidebar />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <h1 className="text-xl md:text-2xl font-bold text-charcoal">{t("provider.notifications", { defaultValue: "Notificaciones" })}</h1>
                <div className="flex gap-2">
                  <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === "all" ? "bg-charcoal text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{t("provider.notif.all")}</button>
                  <button onClick={() => setFilter("unread")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === "unread" ? "bg-charcoal text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{t("provider.notif.unread")}</button>
                  {hasUnread && (
                    <button onClick={onReadAll} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-ocean hover:bg-ocean/5">{t("provider.notif.markAllRead")}</button>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="py-12 text-center text-gray-400 text-sm">{t("provider.loading")}</div>
              ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">{t("provider.notif.empty")}</div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => onOpen(n)}
                      className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-colors ${n.isRead ? "bg-white border border-gray-100" : "bg-ocean/5 border border-ocean/10"}`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${n.isRead ? "bg-gray-100 text-gray-400" : "bg-ocean/10 text-ocean"}`}>
                        <i className={typeIcon(n.type)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`text-sm font-semibold ${n.isRead ? "text-charcoal" : "text-ocean"}`}>{n.title}</h3>
                          <span className="text-xs text-gray-400 shrink-0">{relativeTime(n.createdAt, t)}</span>
                        </div>
                        <p className="text-sm text-gray-600">{n.body}</p>
                      </div>
                      {!n.isRead && <div className="w-2 h-2 rounded-full bg-ocean shrink-0 mt-2" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
